import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { pxToRem } from 'utils/convert';

import { UseCampaignsHasRecentTriggeredAlertsState } from 'modules/campaigns/lib';
import { GeneralEmptyState, NoResults } from '../components/EmptyState';
import { GeneralErrorState } from '../components/ErrorState';
import { SkeletonRows } from '../components/SkeletonRows';
import { GraphQLCampaign } from '../domainTypes';
import { useCampaignUsers } from '../hooks/useCampaignUsers';
import { useHasFilters } from '../hooks/useHasFilters';
import { GraphQLCampaignSubfolder, User } from '../types';
import { CampaignFoldersTableRows } from './CampaignFoldersTableRows';
import { CampaignTableHeader } from './CampaignTableHeader';
import { useCampaignQueryContext } from './contexts/CampaignQueryContext';
import { SavedAlertsByMedium } from '../../campaigns/alerts/types';
import { useProjectCampaignAlertsQuery } from '../../campaigns/alerts/hooks';
import { AlertStatus } from '../../campaigns/alerts/constants';
import { selectIsCampaignPerformanceAlertsFeatureFlagOn } from '../../../core/userContext';

/**
 * Default number of skeleton rows to show when data is being loaded
 */
const DEFAULT_NUMBER_OF_SKELETON_ROWS = 8;

const TableWrapper = styled.div`
  min-width: ${pxToRem(1100)};
`;

type CampaignFoldersTableContentProps = {
  isLoading: boolean;
  hasResponseError: boolean;
  hasEmptyState: boolean;
  hasFilters: boolean;
  hasSearch: boolean;
  folders: GraphQLCampaignSubfolder[];
  campaigns: GraphQLCampaign[];
  userInfoLookup: Record<string, User>;
  numOfSkeletonRows: number;
  campaignTriggeredAlerts?: UseCampaignsHasRecentTriggeredAlertsState['campaignTriggeredAlerts'];
  projectAlerts: SavedAlertsByMedium;
};

const CampaignFoldersTableContent = ({
  isLoading,
  numOfSkeletonRows,
  hasResponseError,
  hasEmptyState,
  hasFilters,
  hasSearch,
  campaigns,
  folders,
  userInfoLookup,
  campaignTriggeredAlerts,
  projectAlerts,
}: CampaignFoldersTableContentProps) => {
  const hasNoResults = hasEmptyState && (hasFilters || hasSearch);
  const generalHasNoResults = hasEmptyState && !hasFilters;
  const hasCampaignFolders = folders?.length + campaigns?.length > 0;

  switch (true) {
    case isLoading:
      return <SkeletonRows numRows={numOfSkeletonRows} />;

    case hasResponseError:
      return <GeneralErrorState />;

    case hasNoResults:
      return <NoResults />;

    case generalHasNoResults:
      return <GeneralEmptyState />;

    case hasCampaignFolders:
      return (
        <CampaignFoldersTableRows
          folders={folders}
          campaigns={campaigns}
          userInfoLookup={userInfoLookup}
          campaignTriggeredAlerts={campaignTriggeredAlerts}
          projectAlerts={projectAlerts}
        />
      );

    default:
      return null; // this should be unreachable
  }
};

export const CampaignFoldersTable = () => {
  const {
    campaigns,
    folders,
    groupRecurringCampaigns,
    isLoading,
    parentCampaign,
    hasResponseError,
    searchQuery,
    campaignTriggeredAlerts,
  } = useCampaignQueryContext();

  const isCampaignPerformanceAlertsFeatureEnabled = useSelector(
    selectIsCampaignPerformanceAlertsFeatureFlagOn,
  );

  const skeletonRowsRef = useRef(DEFAULT_NUMBER_OF_SKELETON_ROWS);

  const campaignsToShow = useMemo(() => {
    let topCampaigns: GraphQLCampaign[] = [];

    if (groupRecurringCampaigns && parentCampaign) {
      const parentIsInListing = campaigns.find((campaign) => campaign.id === parentCampaign.id);

      if (!parentIsInListing) {
        topCampaigns = [parentCampaign];
      }
    }

    return [...topCampaigns, ...campaigns];
  }, [groupRecurringCampaigns, parentCampaign, campaigns]);

  const { userInfoLookup } = useCampaignUsers();

  /* get all enabled-alert settings in project level */
  const projectAlertsQueryOptions = {
    skip: !isCampaignPerformanceAlertsFeatureEnabled,
    variables: {
      status: AlertStatus.Enabled,
    },
  };

  const { results: projectCampaignAlerts } =
    useProjectCampaignAlertsQuery(projectAlertsQueryOptions);

  useEffect(() => {
    if (!isLoading) {
      skeletonRowsRef.current =
        campaigns.length + folders.length || DEFAULT_NUMBER_OF_SKELETON_ROWS;
    }
  }, [isLoading, campaigns.length, folders.length]);

  const hasFilters = useHasFilters();
  const hasSearch = !!searchQuery;
  const hasEmptyState =
    !isLoading && campaigns.length + folders.length + campaignsToShow.length === 0;

  return (
    <TableWrapper data-test="campaign-folder-table">
      <CampaignTableHeader />
      <CampaignFoldersTableContent
        isLoading={isLoading}
        hasEmptyState={hasEmptyState}
        hasResponseError={hasResponseError}
        hasFilters={hasFilters}
        hasSearch={hasSearch}
        folders={folders}
        campaigns={campaignsToShow}
        userInfoLookup={userInfoLookup}
        numOfSkeletonRows={skeletonRowsRef.current}
        campaignTriggeredAlerts={campaignTriggeredAlerts}
        projectAlerts={projectCampaignAlerts}
      />
    </TableWrapper>
  );
};
